import React, { useState, useEffect } from 'react';
import { Container, Typography, TextField, Button, Box } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useMutation, useQuery } from '@tanstack/react-query';
import { sendPasswordResetEmail, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { auth, storage } from '../../firebase/firebase';
import ErrorDialog from '../../components/global/ErrorDialog';
import { WEBSITE_ID } from '../../projectConfig';
import LoadingSpinner from '../../components/global/LoadingSpinner';
import { getCurrentBusinessConfig } from '../../firebase/firebase-delegate';
import { getDownloadURL, ref } from 'firebase/storage';

const AdminLogin: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const { user, loading: loadingAuth } = useAuth();
  const [resetEmailSent, setResetEmailSent] = useState(false);

  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [isLogoReady, setIsLogoReady] = useState(false);


  const navigate = useNavigate();
  const location = useLocation();

  const businessConfigQuery = useQuery({
    queryKey: ['businessConfig'],
    queryFn: () => getCurrentBusinessConfig(),
  });

  useEffect(() => {
    if (user && location.pathname === '/admin') {
      navigate('/admin/schedule-hub', { replace: true });
    }
  }, [user, navigate, location.pathname]);

  const mutation = useMutation({
    mutationFn: async () => {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Retrieve the ID token result to get custom claims
      const idTokenResult = await user.getIdTokenResult();
      const userWebsiteId = idTokenResult.claims.websiteId;

      // Ensure the user has the correct websiteId
      if (!userWebsiteId || userWebsiteId !== WEBSITE_ID) {
        await signOut(auth);
        throw new Error('Unauthorized access: Incorrect website.');
      }

      return userCredential;
    },
    onSuccess: () => navigate('/admin/schedule-hub'),
    onError: (error) => {
      console.error('Login error:', error);
      setError('Login failed. Please try again.');
      setDialogOpen(true);
    },
  });

  const handleForgotPassword = async () => {
    if (!email) {
      setError('Please enter your email first.');
      setDialogOpen(true);
      return;
    }

    try {
      await sendPasswordResetEmail(auth, email, {
        url: `${window.location.origin}/reset-password`,
      });


      setResetEmailSent(true);
    } catch (err) {
      console.error('Password reset error:', err);
      setError('Failed to send password reset email. Please try again.');
      setDialogOpen(true);
    }
  };

  useEffect(() => {
    const loadLogo = async () => {
      const logoImagePath = businessConfigQuery.data?.businessInfo?.logoImagePath;
      if (!logoImagePath) return;

      try {
        const logoDownloadURL = await getDownloadURL(ref(storage, logoImagePath));
        const img = new Image();
        img.src = logoDownloadURL;
        img.onload = () => {
          setLogoUrl(logoDownloadURL);
          setIsLogoReady(true);
        };
      } catch (error) {
        console.error("Failed to load logo image:", error);
      }
    };

    if (businessConfigQuery.data?.businessInfo?.logoImagePath) {
      loadLogo();
    }
  }, [businessConfigQuery.data?.businessInfo?.logoImagePath]);



  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    mutation.mutate();
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const isLoading = businessConfigQuery.isLoading || businessConfigQuery.isFetching || mutation.isPending || loadingAuth;

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      {isLogoReady && logoUrl && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          mb={3}
          sx={{
            backgroundColor: 'black',
            borderRadius: 2,
            padding: 2,
            width: 'fit-content',
            mx: 'auto',
          }}
        >
          <img
            src={logoUrl}
            alt="Logo"
            style={{ height: 80, objectFit: 'contain', backgroundColor: 'transparent' }}
          />
        </Box>
      )}


      <Typography variant="h5" align="center" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
        ADMIN LOGIN
      </Typography>
      <Box component="form" onSubmit={handleLogin}
        sx={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', width: { xs: '80%', sm: '60%', md: '60%', lg: '50%' },
          mx: 'auto' // Centers the box horizontally 
        }}>
        <TextField
          label="Email"
          variant="outlined"
          fullWidth
          sx={{ mb: 2 }}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <TextField
          label="Password"
          type="password"
          variant="outlined"
          fullWidth
          sx={{ mb: 3 }}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <Typography
          variant="body2"
          sx={{ mb: 2, cursor: 'pointer', color: 'primary.main' }}
          onClick={handleForgotPassword}
        >
          Forgot Password?
        </Typography>
        <Button variant="contained" color="primary" type="submit" sx={{ width: '50%' }}>
          Sign In
        </Button>
      </Box>
      <ErrorDialog open={dialogOpen} onClose={handleCloseDialog} message={error} />
      {resetEmailSent && (
        <Typography variant="body2" color="success.main" align="center" sx={{ mt: 2 }}>
          Password reset email sent!
        </Typography>
      )}

    </Container>
  );
};

export default AdminLogin;
